import React, { useRef, useEffect } from 'react';
import { getRendererByKey } from '../games/proceduralBackgrounds';

/**
 * ProceduralThumbnail — renders a small animated preview of a procedural background.
 * Used in the asset library picker to show what the background actually looks like.
 * Each thumbnail runs its own lightweight animation loop.
 *
 * Time is scaled to 0.25x so the animation feels gentle at thumbnail size.
 */
const THUMBNAIL_TIME_SCALE = 0.25;

export default function ProceduralThumbnail({ rendererKey, className = '', isStatic = false }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({});
  const startRef = useRef(null);

  useEffect(() => {
    const renderer = getRendererByKey(rendererKey);
    if (!renderer) return;

    const state = stateRef.current;

    const draw = (timestamp) => {
      if (startRef.current == null) startRef.current = timestamp;
      const cvs = canvasRef.current;
      if (!cvs) return;
      const ctx = cvs.getContext('2d');
      const rect = cvs.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      if (!w || !h) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      const dpr = window.devicePixelRatio || 1;
      const bw = Math.round(w * dpr);
      const bh = Math.round(h * dpr);
      if (cvs.width !== bw || cvs.height !== bh) {
        cvs.width = bw;
        cvs.height = bh;
      }

      // Static mode: render a single frame at a fixed time to show a nice composition
      const time = isStatic ? 5 : ((timestamp - startRef.current) / 1000) * THUMBNAIL_TIME_SCALE;

      ctx.save();
      ctx.scale(dpr, dpr);
      renderer(ctx, w, h, time, state, null);
      ctx.restore();

      if (!isStatic) {
        rafRef.current = requestAnimationFrame(draw);
      }
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [rendererKey, isStatic]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}
